import type { DefaultValues, Path, UseFormReturn } from 'react-hook-form';
import { useForm } from 'react-hook-form';

import type { IContentBlockFormBodyProps } from '~/components/molecules/ContentBlock';
import type { IContainerProps } from '~/components/utils/Container';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { useAppCurrentFeaturePlanQuery } from '~/lib/api/modules/Apps/hooks/useAppCurrentFeaturePlanQuery';
import { GeneralSettingsFormAdminArticlesSection } from './GeneralSettingsFormAdminArticlesSection';
import { GeneralSettingsFormAllArticlesSection } from './GeneralSettingsFormAllArticlesSection';
import { GeneralSettingsFormGuestArticlesSection } from './GeneralSettingsFormGuestArticlesSection';
import { GeneralSettingsFormMiscSection } from './GeneralSettingsFormMiscSection';
import { GeneralSettingsFormUsersSection } from './GeneralSettingsFormUsersSection';

export type IGeneralSettingsFormValues = {
  aiModerationEnabled?: boolean;
  usersEnabled?: boolean;
  biometrics?: boolean;
  communityArticleCommentsEnabled?: boolean;
  communityArticleShareEnabled?: boolean;
  displayArticleAuthor?: boolean;
  displayArticleCommentsCount?: boolean;
  displayArticleLikesViews?: boolean;
  displayPopularCategories?: boolean;
  displayTabsNames?: boolean;
  feedArticleCommentsEnabled?: boolean;
  feedArticleShareEnabled?: boolean;
  geolocation?: boolean;
  loginArticleRequired?: boolean;
  loginWithUsername?: string;
  phoneRegisterEnabled?: boolean;
  phoneRegisterRequired?: boolean;
  signInWithAppleEnabled?: boolean;
  signInWithApple?: boolean;
  signInWithFacebookEnabled?: boolean;
  signInWithFacebook?: boolean;
  signInWithSAML?: boolean;
  signInWithCrowdaa?: boolean;
  registerWithCrowdaa?: boolean;
  forgotPasswordEnabled?: boolean;
  chatEnabled?: boolean;
  chatNotificationsEnabled?: boolean;
  moderationRequired?: boolean;
};

export type IGeneralSettingsFormProps = IContainerProps &
  Omit<
    IContentBlockFormBodyProps<IGeneralSettingsFormValues>,
    | 'form'
    | 'children'
    | 'onSubmit'
    | 'submitting'
    | 'submitAction'
    | 'submitActionLabel'
  > & {
    defaultValues?: DefaultValues<IGeneralSettingsFormValues>;
    submittingFields?: Array<Path<IGeneralSettingsFormValues>>;
    onChange?: (
      form: UseFormReturn<IGeneralSettingsFormValues>,
      updatedFields?: Array<Path<IGeneralSettingsFormValues>>,
    ) => void;
    appId: string;
  };

export const GeneralSettingsForm: React.FC<IGeneralSettingsFormProps> = (
  props,
) => {
  const { sx, defaultValues, submittingFields, onChange, appId, ...other } =
    props;
  const form = useForm<IGeneralSettingsFormValues>({
    defaultValues,
  });
  const currentFeaturePlanQuery = useAppCurrentFeaturePlanQuery({
    appId,
  });
  const features = currentFeaturePlanQuery.data?.features;

  return (
    <ContentBlock.FormBody sx={sx} form={form} {...other}>
      <GeneralSettingsFormUsersSection
        form={form}
        onChange={onChange}
        submittingFields={submittingFields}
      />
      <GeneralSettingsFormAllArticlesSection
        form={form}
        onChange={onChange}
        submittingFields={submittingFields}
      />
      <GeneralSettingsFormAdminArticlesSection
        form={form}
        onChange={onChange}
        submittingFields={submittingFields}
      />
      <GeneralSettingsFormGuestArticlesSection
        form={form}
        onChange={onChange}
        submittingFields={submittingFields}
        proFeature={!features?.crowd}
      />
      <GeneralSettingsFormMiscSection
        form={form}
        onChange={onChange}
        submittingFields={submittingFields}
      />
    </ContentBlock.FormBody>
  );
};
